import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Settings, X, Mail, Bell, Users, Save } from 'lucide-react';

// Mock Data for Notification Templates
const initialTemplates = [
  { id: 1, name: 'Welcome Onboarding Email', type: 'Email', modalStyle: 'Zoho', status: 'Active', lastModified: '2025-10-20' },
  { id: 2, name: 'Leave Request Approved SMS', type: 'SMS', modalStyle: 'Keka', status: 'Active', lastModified: '2025-10-18' },
  { id: 3, name: 'Monthly Pay Slip Alert', type: 'Push', modalStyle: 'Zoho', status: 'Draft', lastModified: '2025-10-15' },
  { id: 4, name: 'Policy Update Announcement', type: 'Email', modalStyle: 'Keka', status: 'Active', lastModified: '2025-10-10' },
];

// --- Custom Modal Components ---

// 1. Zoho Style Modal (Clean, vibrant blue accents, spacious)
const ZohoModal = ({ template, onClose, onSave }) => {
  const [content, setContent] = useState(template.content || `Hello {{EmployeeName}},\n\nYour ${template.name} has been processed.`);
  const [subject, setSubject] = useState(template.subject || template.name);

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center p-4 font-roboto">
      {/* UPDATED: Reduced max-w to max-w-xl and max-h to max-h-[80vh] 
        to ensure better containment and reduce height. 
      */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col transform transition-all duration-300">
        
        {/* Header - Fixed Height */}
        <div className="p-6 border-b border-blue-100 flex justify-between items-center bg-blue-50 rounded-t-xl flex-shrink-0">
          <h2 className="text-xl font-bold text-blue-700 font-poppins">
            <Mail className="inline-block w-5 h-5 mr-2" />
            Edit Template: {template.name}
          </h2>
          <button onClick={onClose} className="text-blue-500 hover:text-blue-700 transition">
            <X size={20} />
          </button>
        </div>

        {/* Content Area - Scrollable (Takes up remaining height) */}
        <div className="p-6 space-y-4 flex-grow overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject / Title</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2 border border-blue-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 font-roboto"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Content (Use {'{{Variable}}'} for dynamic data)</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows="8"
              className="w-full p-3 border border-blue-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 font-roboto resize-none"
              placeholder="e.g., Dear {{EmployeeName}}, your leave is approved."
            ></textarea>
          </div>
          <div className="flex justify-between items-center text-sm text-gray-500 pt-2">
            <p className='flex items-center'>
              <Users className="w-4 h-4 mr-1 text-blue-500" /> Target: All Employees
            </p>
            <p>Type: {template.type}</p>
          </div>
          {/* Filler content to test scrolling fix */}
          <div className="text-xs text-gray-400 p-2 border border-dashed rounded">
              <p>Template Style: Classic HR Platform</p>
              <p>Note: This area would contain advanced settings or version history in a real app.</p>
              <p className="mt-2">Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content.</p>
          </div>
        </div>

        {/* Footer - Fixed Height */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-xl flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition font-poppins"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ ...template, content, subject })}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 transition font-poppins flex items-center"
          >
            <Save className="w-4 h-4 mr-2" /> Save Template
          </button>
        </div>
      </div>
    </div>
  );
};

// 2. Keka Style Modal (Minimalist, dark header, focused structure)
const KekaModal = ({ template, onClose, onSave }) => {
    const [content, setContent] = useState(template.content || `[${template.type}] Your {{Event}} update for ${template.name}.`);
    const [title, setTitle] = useState(template.title || template.name);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 font-roboto">
        {/* UPDATED: Reduced max-w to max-w-lg and max-h to max-h-[80vh] 
          to ensure better containment and reduce height. 
        */}
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col transform transition-all duration-300 overflow-hidden">
          
          {/* Dark Header Area - Fixed Height */}
          <div className="p-4 bg-gray-800 flex justify-between items-center flex-shrink-0">
            <h2 className="text-lg font-semibold text-white font-poppins">
              <Bell className="inline-block w-5 h-5 mr-2 text-yellow-300" />
              Template Designer
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition">
              <X size={18} />
            </button>
          </div>

          {/* Content Area - Scrollable (Takes up remaining height) */}
          <div className="p-6 space-y-5 flex-grow overflow-y-auto">
            {/* Template Title Input */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-yellow-500 focus:border-yellow-500 font-roboto text-sm"
              />
            </div>
            {/* Template Content Area */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">Message Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows="6" 
                className="w-full p-3 border border-gray-300 rounded focus:ring-yellow-500 focus:border-yellow-500 font-roboto text-sm resize-none"
                placeholder="Content goes here. Variables like {employee_id} can be used."
              ></textarea>
            </div>
            <div className="flex justify-between text-xs text-gray-500 italic border-t pt-3">
                <p>Format: {template.type}</p>
                <p>Style: Modern HRIS Style</p>
            </div>
            {/* Filler content to test scrolling fix */}
            <div className="text-xs text-gray-400 p-2 border border-dashed rounded">
                <p>Template Style: Modern HRIS Style</p>
                <p>Note: This area would contain advanced settings or version history in a real app.</p>
                <p className="mt-2">Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content. Scroll Check Filler Content.</p>
            </div>
          </div>

          {/* Action Footer - Fixed Height */}
          <div className="p-4 border-t border-gray-200 flex justify-end space-x-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 rounded hover:bg-gray-100 transition font-poppins"
            >
              Close
            </button>
            <button
              onClick={() => onSave({ ...template, content, title })}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-yellow-600 rounded hover:bg-yellow-700 transition shadow-md font-poppins flex items-center"
            >
              <Save className="w-3 h-3 mr-2" /> Deploy Changes
            </button>
          </div>
        </div>
      </div>
    );
};


// --- Main Application Component ---

const App = () => {
  const [templates, setTemplates] = useState(initialTemplates);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);

  // Function to handle opening the correct modal
  const openEditModal = useCallback((template) => {
    setActiveTemplate(template);
    setIsModalOpen(true);
  }, []);

  // Function to handle saving template data
  const handleSaveTemplate = useCallback((updatedTemplate) => {
    setTemplates(prev =>
      prev.map(t => (t.id === updatedTemplate.id ? updatedTemplate : t))
    );
    setIsModalOpen(false);
    setActiveTemplate(null);
    console.log('Template Saved:', updatedTemplate);
  }, []);

  // Render the appropriate modal based on the active template's style
  const renderModal = () => {
    if (!activeTemplate) return null;

    const ModalComponent = activeTemplate.modalStyle === 'Zoho' ? ZohoModal : KekaModal;

    return (
      <ModalComponent
        template={activeTemplate}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTemplate}
      />
    );
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-700';
      case 'Draft':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 font-roboto">
        {/* Tailwind Config for Fonts (Hidden in JSX output but necessary for single file build) */}
        <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;700&display=swap');
        `}</style>
        <script>{`
            tailwind.config = {
              theme: {
                extend: {
                  fontFamily: {
                    poppins: ['Poppins', 'sans-serif'],
                    roboto: ['Roboto', 'sans-serif'],
                  }
                }
              }
            }
        `}</script>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 font-poppins">Notification Templates</h1>
          <div className="flex space-x-3">
            <button className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-xl shadow-sm hover:bg-gray-100 transition font-poppins text-sm">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
            <button
              onClick={() => openEditModal({ id: Date.now(), name: 'New Template', type: 'Email', modalStyle: 'Zoho', status: 'Draft' })}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl shadow-lg hover:bg-indigo-700 transition font-poppins text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </button>
          </div>
        </header>

        {/* Templates Table/List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">Template Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins hidden sm:table-cell">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins hidden md:table-cell">Last Modified</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-indigo-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 font-poppins">
                    {template.name}
                    <div className="md:hidden text-xs text-gray-500 mt-0.5">
                      {template.type} &middot; {template.lastModified}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        template.type === 'Email' ? 'bg-blue-100 text-blue-700' :
                        template.type === 'SMS' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                    }`}>
                        {template.type === 'Email' ? <Mail size={12} className="mr-1" /> : <Bell size={12} className="mr-1" />}
                        {template.type}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell font-roboto">
                    {template.lastModified}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClasses(template.status)} font-roboto`}>
                      {template.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(template)}
                      className="text-indigo-600 hover:text-indigo-900 font-poppins text-sm p-1 rounded hover:bg-indigo-100 transition"
                    >
                      Edit Template
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {templates.length === 0 && (
            <div className="p-10 text-center text-gray-500 font-roboto">
              No templates found. Click "New Template" to start.
            </div>
          )}
        </div>
      </div>

      {/* Modal Renderer */}
      {isModalOpen && renderModal()}
    </div>
  );
};

export default App;